<template>
  <div class="scores-sorting">
    <draggable
        v-model="localPlan"
        :group="{ name: 'scores-sorting' }"
        handle=".drag-handle"
        :animation="150"
        :force-fallback="true"
        ghost-class="is-ghost"
        chosen-class="is-chosen"
        drag-class="is-dragging"
        @start="onDragStart"
        @end="onDragEnd"
        @change="emitPlan">
      <transition-group type="transition" name="flip-list">
        <div v-for="(s, i) in localPlan"
            :key="`sort-${s.key || 'empty'}-${i}`"
            class="mb-1 sort-level">


          <b-input-group class="input-group-merge">
            <b-input-group-prepend
                is-text
                class="drag-handle cursor-move"
                title="Drag to reorder">
              <b-icon-grip-vertical />
            </b-input-group-prepend>

            <v-select
                :id="`sort-field-${s.key}-${i}`"
                v-model="s.key"
                :options="fields"
                label="text"
                :reduce="o => o.key"
                :select-on-tab="true"
                :dir="dir"
                append-to-body
                :calculate-position="withPopper"
                class="font-small-3 d-print-none table-select"
                @input="emitPlan"/>

            <b-input-group-append>
              <b-button
                  :title="s.dir === 'asc' ? 'Ascending' : 'Descending'"
                  size="sm"
                  variant="outline-primary"
                  @click.stop.prevent="toggleDir(i)">
                <b-icon :icon="getIcon(s.key, s.dir)" />
              </b-button>

              <b-button
                  title="Remove level"
                  size="sm"
                  variant="outline-danger"
                  @click.stop.prevent="removeLevel(i)">
                <b-icon icon="trash" />
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </transition-group>
    </draggable>

    <b-button variant="outline-primary" block size="sm" @click="addField">
      <b-icon icon="plus" class="mr-50" /> Add Sort Field
    </b-button>
    <div class="d-flex gap-1">
      <b-button v-if="defaultPlan.length" variant="outline-secondary" block size="sm" class="mt-50 w-100" @click="reset">
        <b-icon icon="arrow-counterclockwise" class="mr-50" /> Reset
      </b-button>
      <b-button :disabled="!localPlan.length" variant="outline-danger" block size="sm" class="mt-50 w-100" @click="clear">
        <b-icon icon="trash" class="mr-50" /> Clear
      </b-button>
    </div>


    <template v-if="presets.length">
      <b-dropdown-divider class="mt-2 mb-1"/>
      <b-dropdown-group header="Presets" header-classes="px-0">
        <b-button-group vertical class="w-100">
          <b-button
              v-for="p in presets"
              :key="p.id || p.label"
              variant="outline-primary"
              size="sm"
              :pressed="isActivePreset(p)"
              class="text-left"
              @click="applyPreset(p)">
            <b-icon v-if="p.icon" :icon="p.icon" class="mr-50" />
            {{ p.label }}
          </b-button>
        </b-button-group>
      </b-dropdown-group>
    </template>
  </div>
</template>

<script>
  import vSelect from 'vue-select'
  import draggable from 'vuedraggable'

  export default {
    name: 'TableSorting',
    components: { vSelect, draggable },
    props: {
      value: { type: Array, default: () => [] },
      fields: { type: Array, required: true },
      presets: { type: Array, default: () => [] },
      dir: { type: String, default: 'ltr' },
      withPopper: { type: Function, default: null },
    },
    data() {
      return {
        defaultPlan: this.clonePlan(this.value),
        localPlan: this.clonePlan(this.value),
        isDragging: false,
      }
    },
    watch: {
      value: {
        deep: true,
        handler(newVal) {
          // keep local in sync if parent overwrites plan (load settings / reset / etc.)
          this.localPlan = this.clonePlan(newVal)
        },
      },
    },
    methods: {
      getIcon(fieldKey, dir) {
        const d = dir === 'desc' ? 'desc' : 'asc'
        const field = this.fields.find((f) => String(f.key) === String(fieldKey))
        const t = field?.type ? String(field.type).toLowerCase() : 'string'

        if (t === 'number' || t === 'numeric' || t === 'int' || t === 'float') {
          return d === 'asc' ? 'sort-numeric-down' : 'sort-numeric-down-alt'
        }
        if (t === 'date' || t === 'datetime' || t === 'time') {
          return d === 'asc' ? 'sort-down' : 'sort-down-alt'
        }
        return d === 'asc' ? 'sort-alpha-down' : 'sort-alpha-down-alt'
      },

      clonePlan(plan) {
        return (plan || []).map((s) => ({
          key: s?.key ?? null,
          dir: s?.dir === 'desc' ? 'desc' : 'asc',
        }))
      },

      emitPlan() {
        // emit a *new array* so Vue 2 always notices
        const out = (this.localPlan || []).map((s) => ({ key: s.key, dir: s.dir }))
        this.$emit('input', out)
        this.$emit('change', out)
      },

      normalizePlan(plan) {
        // ensures only valid shape + normalized dir
        return (plan || []).map(s => ({
          key: s?.key ?? null,
          dir: s?.dir === 'desc' ? 'desc' : 'asc',
        })).filter(s => !!s.key)
      },

      applyPreset(preset) {
        const plan = this.normalizePlan(preset?.plan)
        this.localPlan = this.clonePlan(plan)
        this.emitPlan()
      },

      addField() {
        const firstKey = this.fields?.[0]?.key || 'student.name.last'
        this.localPlan.push({ key: firstKey, dir: 'asc' })
        this.emitPlan()
      },

      removeLevel(i) {
        if (this.localPlan.length > 0) {
          this.localPlan.splice(i, 1)
          this.emitPlan()
        }
      },

      toggleDir(i) {
        const row = this.localPlan[i]
        if (!row) return
        row.dir = row.dir === 'asc' ? 'desc' : 'asc'
        this.emitPlan()
      },

      reset() {
        const fallback = this.defaultPlan && this.defaultPlan.length ? this.defaultPlan : []
        this.localPlan = this.clonePlan(fallback)
        this.emitPlan()
      },

      clear() {
        this.localPlan = []
        this.emitPlan()
      },

      onDragStart() {
        this.isDragging = true
      },
      onDragEnd() {
        this.isDragging = false
        // emitPlan already fires on @change, but @end is a safe backstop
        this.emitPlan()
      },

      isActivePreset(preset) {
        if (!preset?.plan?.length) return false
        const localPlanKeys = (this.localPlan || []).map(s => String(s?.key))
        const presetPlanKeys = (preset.plan || []).map(p => String(p?.key))

        if (localPlanKeys.length !== presetPlanKeys.length) return false
        return localPlanKeys.every((key, i) => key === presetPlanKeys[i])
      }
    }
  }
</script>

<style scoped>
.cursor-move {
  cursor: move;
}

.sort-level.is-dragging {
  opacity: 0.65;
}

/* vuedraggable classes */
.is-ghost {
  opacity: 0.35;
}

.is-chosen {
  opacity: 0.8;
}

/* optional subtle animation on reorder */
.flip-list-move {
  transition: transform 150ms;
}
</style>
